import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Use current domain for API calls - works with custom domains
const API_BASE = window.location.origin;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API_BASE}/api/auth/login`, { email, password }, { withCredentials: true });
    // 2FA: don't set user yet — return challenge token to be handled by login page
    if (data?.requires_2fa) {
      return data;
    }
    setUser(data);
    return data;
  };

  const verify2FA = async (challenge_token, code) => {
    const { data } = await axios.post(`${API_BASE}/api/auth/2fa/verify`, { challenge_token, code }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const register = async (data) => {
    const { data: result } = await axios.post(`${API_BASE}/api/auth/register`, data, { withCredentials: true });
    return result;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore logout API errors
    }
    setUser(null);
    window.location.href = '/login';
  };

  // Role helpers
  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAssistant = user?.role === 'assistant';
  const isAdminOrAbove = isOwner || isAssistant;
  const isStaff = user?.role === 'staff';
  const isDistributor = user?.role === 'distributor';
  const isSalesPerson = user?.role === 'sales_person';
  const isAccountant = user?.role === 'accountant';

  const contextValue = useMemo(() => ({
    user, loading, login, verify2FA, register, logout, checkAuth,
    isOwner, isAssistant, isAdminOrAbove, isStaff, isDistributor, isSalesPerson, isAccountant
  }), [user, loading, isOwner, isAssistant, isAdminOrAbove, isStaff, isDistributor, isSalesPerson, isAccountant]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
